import React, { useState, useEffect } from 'react'
import '../../assets/styles/dashboard.css';
import '../../assets/styles/new-data.css';
import '../../assets/styles/myaccount.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import commonService from '../../services/otherServices';
import Pagination from '../../components/Common/Pagination';
import Popup from '../../components/Common/Error'

const SubUsers = () => {
    const [subUsers, setSubUsers] = useState<any[]>([]);
    const [isListLoading, setIsListLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);

    const [form, setForm] = useState({ first_name: "", last_name: "", email: "" });

    const [pageDetail, setPageDetail] = useState({
        page: 1,
        total_pages: 1,
        total_records: 0,
        page_size: 10,
    });

    const isFormComplete = () =>
        form.first_name.trim() !== "" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());

    const loadPage = async (page: number) => {
        if (isListLoading) return;
        try {
            setIsListLoading(true);
            const response = await commonService.listSubUsers({ page, page_size: pageDetail.page_size });
            setSubUsers(response?.data?.items || []);
            setPageDetail({
                page: response.data.page,
                total_pages: response.data.total_pages,
                total_records: response.data.total,
                page_size: response.data.page_size,
            });
        } catch (error) {
            console.error("Error fetching sub-users:", error);
        } finally {
            setIsListLoading(false);
        }
    };

    useEffect(() => {
        loadPage(1);
    }, []);

    const handleAddSubUser = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isFormComplete() || isSubmitting) return;
        setIsSubmitting(true);
        try {
            await commonService.createSubUser(form);
            setForm({ first_name: "", last_name: "", email: "" });
            setShowSuccessPopup(true);
            loadPage(1);
        } catch (err: any) {
            setErrorMsg(err?.response?.data?.message || "Failed to add sub-user.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const toggleStatus = async (item: any) => {
        try {
            if (item.account_status === "active") {
                await commonService.deactivateSubUser(item.id);
            } else {
                await commonService.activateSubUser(item.id);
            }
            loadPage(pageDetail.page);
        } catch (err) {
            setErrorMsg("Failed to update sub-user status.");
        }
    };

    const removeSubUser = async (item: any) => {
        if (!window.confirm(`Remove ${item.email}? This cannot be undone.`)) return;
        try {
            await commonService.deleteSubUser(item.id);
            loadPage(pageDetail.page);
        } catch (err) {
            setErrorMsg("Failed to remove sub-user.");
        }
    };

    return (
        <>
            <div className="container">
                <div>
                    <p className="your-data">Sub Users</p>
                    <p className="track">Add sub-users to your account - they can view and download the exact same data you can.</p>
                </div>

                <div className="data-form-white-box">
                    <form onSubmit={handleAddSubUser} className="row g-3">
                        <div className="col-md-4">
                            <div className="dashboard-input-group" style={{ maxWidth: "100%" }}>
                                <label className="dashboard-input-label">First Name *</label>
                                <input
                                    type="text"
                                    className="dashboard-input"
                                    placeholder="Enter first name"
                                    required
                                    value={form.first_name}
                                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="dashboard-input-group" style={{ maxWidth: "100%" }}>
                                <label className="dashboard-input-label">Last Name</label>
                                <input
                                    type="text"
                                    className="dashboard-input"
                                    placeholder="Enter last name"
                                    value={form.last_name}
                                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="dashboard-input-group" style={{ maxWidth: "100%" }}>
                                <label className="dashboard-input-label">Email *</label>
                                <input
                                    type="email"
                                    className="dashboard-input"
                                    placeholder="Enter email address"
                                    required
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="col-12 d-flex justify-content-start mt-2">
                            <button
                                type="submit"
                                className={`submit-request-btn ${isFormComplete() ? 'active' : ''}`}
                                disabled={!isFormComplete() || isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                        Adding...
                                    </>
                                ) : "Add Sub User"}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="dashboard-table-wrapper" style={{ marginTop: "1.5rem" }}>
                    <table className="dashboard-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Status</th>
                                <th>Added On</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isListLoading && (
                                <tr>
                                    <td colSpan={5} className="text-center py-3">
                                        <span className="spinner-border spinner-border-sm me-2" />
                                        Loading...
                                    </td>
                                </tr>
                            )}
                            {!isListLoading && subUsers.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="text-center py-3">
                                        No sub-users added yet.
                                    </td>
                                </tr>
                            )}
                            {subUsers.map((item) => (
                                <tr key={item.id}>
                                    <td>{item.first_name} {item.last_name || ""}</td>
                                    <td>{item.email}</td>
                                    <td
                                        style={{
                                            color: item.account_status === "active" ? "#0CAF60" : "#787878",
                                            fontWeight: 500,
                                            textTransform: "capitalize",
                                        }}
                                    >
                                        {item.account_status}
                                    </td>
                                    <td>{item.created_at ? new Date(item.created_at).toISOString().split('T')[0] : '--'}</td>
                                    <td>
                                        <div className="d-flex flex-row justify-content-center gap-2">
                                            <button
                                                type="button"
                                                className={item.account_status === "active" ? "updateafter-90" : "update"}
                                                onClick={() => toggleStatus(item)}
                                            >
                                                {item.account_status === "active" ? "Deactivate" : "Activate"}
                                            </button>
                                            <button
                                                type="button"
                                                className="delete-profile"
                                                style={{ margin: 0 }}
                                                onClick={() => removeSubUser(item)}
                                            >
                                                <img src="/images/active-delete.png" width="12" alt="" /> Remove
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="dashboard-pagination-wrapper">
                    <Pagination
                        page={pageDetail.page}
                        total_pages={pageDetail.total_pages}
                        total_records={pageDetail.total_records}
                        page_size={pageDetail.page_size}
                        onPageChange={(newPage) => loadPage(newPage)}
                    />
                </div>
            </div>

            <Popup
                isOpen={!!errorMsg}
                onClose={() => setErrorMsg("")}
                title={errorMsg}
                iconSrc="/images/grey-cancel-cta.png"
            />

            {showSuccessPopup && (
                <div className="submit-popup-overlay">
                    <div className="submit-popup">
                        <div className="d-flex justify-content-end">
                            <img
                                src="/images/close-active.png"
                                alt=""
                                style={{ cursor: "pointer" }}
                                onClick={() => setShowSuccessPopup(false)}
                            />
                        </div>
                        <div className="d-flex justify-content-center">
                            <img src="/images/hand-shak.png" alt="" />
                        </div>
                        <p className="submit-popup-title">Sub-User Added Successfully!</p>
                        <p className="submit-popup-text" style={{ textAlign: "center" }}>
                            They'll receive an email with a link to set their own password.
                        </p>
                    </div>
                </div>
            )}
        </>
    );
};

export default SubUsers;
